import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { pageTransition, formatDate } from "@/lib/utils";
import { usePermissions } from "@/hooks/usePermissions";
import { useToast } from "@/hooks/useToast";
import { getApprovalQueue, getAllApprovals, resolveApproval, bulkResolve } from "@/api/approvals";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Tabs } from "@/components/ui/Tabs";
import { EmptyState } from "@/components/shared/EmptyState";
import { SavedViewsMenu } from "@/components/shared/SavedViewsMenu";
import { useSavedViews } from "@/hooks/useSavedViews";
import { SkeletonRow } from "@/components/ui/Skeleton";
import { Check, X, CheckSquare } from "lucide-react";
import type { ApprovalRequest, ApprovalStatus } from "@/types/approval";
import { APPROVAL_TYPE_LABELS } from "@/types/approval";
import type { PaginatedResponse } from "@/types/common";

export function ApprovalsPage() {
  const { hasRole } = usePermissions();
  const { addToast } = useToast();

  const [activeTab, setActiveTab] = useState<ApprovalStatus | "ALL">("PENDING");
  const [data, setData] = useState<PaginatedResponse<ApprovalRequest> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const isAdmin = hasRole("SUPER_ADMIN", "HR_ADMIN");

  type ApprovalsFilterSnapshot = { activeTab: ApprovalStatus | "ALL" };
  const savedViews = useSavedViews<ApprovalsFilterSnapshot>({
    scope: "approvals",
    filters: { activeTab },
    defaultFilters: { activeTab: "PENDING" },
    onApply: (f) => setActiveTab(f.activeTab),
  });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const status = activeTab === "ALL" ? undefined : activeTab;
      const result = isAdmin
        ? await getAllApprovals(1, 50, status)
        : await getApprovalQueue(1, 50, status);
      setData(result);
    } catch {
      addToast("error", "Failed to load approvals");
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, isAdmin, addToast]);

  useEffect(() => {
    fetchData();
    setSelectedIds(new Set());
  }, [fetchData]);

  // Optimistic resolve: flip the row's status immediately so the reviewer
  // sees the queue shrink without waiting for the round-trip. On failure
  // we restore the pre-update snapshot and surface a toast — no fetch
  // needed in the happy path since the server is the only thing that
  // could disagree with us, and we already asked it to agree.
  const handleResolve = async (id: string, status: ApprovalStatus) => {
    const snapshot = data;
    setData((prev) =>
      prev
        ? {
            ...prev,
            items: prev.items.map((a) =>
              a.id === id
                ? { ...a, status, resolved_at: new Date().toISOString() }
                : a
            ),
          }
        : prev
    );
    try {
      await resolveApproval(id, status);
      addToast("success", `Approval ${status.toLowerCase()}`);
    } catch {
      setData(snapshot);
      addToast("error", "Failed to update approval");
    }
  };

  const handleBulkResolve = async (status: ApprovalStatus) => {
    if (selectedIds.size === 0) return;
    const snapshot = data;
    const ids = Array.from(selectedIds);
    const idSet = new Set(ids);
    const now = new Date().toISOString();
    setData((prev) =>
      prev
        ? {
            ...prev,
            items: prev.items.map((a) =>
              idSet.has(a.id) ? { ...a, status, resolved_at: now } : a
            ),
          }
        : prev
    );
    setSelectedIds(new Set());
    try {
      await bulkResolve(ids, status);
      addToast("success", `${ids.length} approvals ${status.toLowerCase()}`);
    } catch {
      setData(snapshot);
      setSelectedIds(new Set(ids));
      addToast("error", "Failed to bulk update");
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const pending = (data?.items ?? []).filter((a) => a.status === "PENDING");
    if (selectedIds.size === pending.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pending.map((a) => a.id)));
    }
  };

  const tabs = [
    { id: "PENDING", label: "Pending", count: data?.items.filter((a) => a.status === "PENDING").length },
    { id: "APPROVED", label: "Approved" },
    { id: "DENIED", label: "Denied" },
    { id: "ALL", label: "All" },
  ];

  const pendingItems = (data?.items ?? []).filter((a) =>
    activeTab === "ALL" ? true : a.status === activeTab
  );

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Approvals"
        subtitle="Review and manage pending requests"
        actions={
          selectedIds.size > 0 ? (
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleBulkResolve("APPROVED")}
              >
                <Check className="h-4 w-4" />
                Approve ({selectedIds.size})
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleBulkResolve("DENIED")}
              >
                <X className="h-4 w-4" />
                Deny ({selectedIds.size})
              </Button>
            </div>
          ) : undefined
        }
      />

      <div className="flex items-center justify-between mb-6 gap-3">
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={(id) => setActiveTab(id as ApprovalStatus | "ALL")}
        />
        <SavedViewsMenu
          views={savedViews.views}
          activeView={savedViews.activeView}
          hasUnsavedChanges={savedViews.hasUnsavedChanges}
          isDefault={savedViews.isDefault}
          onApply={savedViews.applyView}
          onSave={savedViews.saveView}
          onDelete={savedViews.deleteView}
          onRename={savedViews.renameView}
          onUpdateActive={savedViews.updateActiveView}
          onClear={savedViews.clear}
        />
      </div>

      {isLoading ? (
        <Card padding={false}>
          <div className="p-4 space-y-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonRow key={i} />
            ))}
          </div>
        </Card>
      ) : pendingItems.length === 0 ? (
        <EmptyState
          icon={CheckSquare}
          title="No approvals"
          description={
            activeTab === "PENDING"
              ? "You're all caught up! No pending approvals."
              : `No ${activeTab.toLowerCase()} approvals found.`
          }
        />
      ) : (
        <div className="space-y-3">
          {activeTab === "PENDING" && pendingItems.length > 0 && (
            <div className="flex items-center gap-2 mb-2">
              <button
                onClick={toggleSelectAll}
                className="text-xs text-accent hover:text-accent-600"
              >
                {selectedIds.size === pendingItems.length ? "Deselect all" : "Select all"}
              </button>
            </div>
          )}

          {pendingItems.map((approval) => (
            <Card key={approval.id} className="flex items-center gap-4">
              {approval.status === "PENDING" && (
                <input
                  type="checkbox"
                  checked={selectedIds.has(approval.id)}
                  onChange={() => toggleSelect(approval.id)}
                  className="rounded border-border text-accent focus:ring-accent shrink-0"
                />
              )}

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Badge
                    variant={
                      approval.type === "TIME_OFF" ? "info" :
                      approval.type === "EXPENSE" ? "warning" : "default"
                    }
                  >
                    {APPROVAL_TYPE_LABELS[approval.type]}
                  </Badge>
                  <Badge
                    variant={
                      approval.status === "APPROVED" ? "success" :
                      approval.status === "DENIED" ? "danger" :
                      approval.status === "PENDING" ? "warning" : "default"
                    }
                  >
                    {approval.status}
                  </Badge>
                </div>
                <p className="font-medium text-text-primary">{approval.title}</p>
                <div className="flex items-center gap-3 mt-1 text-xs text-text-muted">
                  <span>From: {approval.requestor_name || "Unknown"}</span>
                  {approval.description && <span>{approval.description}</span>}
                  <span>{formatDate(approval.created_at, "MMM d, yyyy")}</span>
                </div>
              </div>

              {approval.status === "PENDING" && (
                <div className="flex gap-2 shrink-0">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleResolve(approval.id, "APPROVED")}
                  >
                    <Check className="h-4 w-4" />
                    Approve
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleResolve(approval.id, "DENIED")}
                  >
                    <X className="h-4 w-4" />
                    Deny
                  </Button>
                </div>
              )}

              {approval.status !== "PENDING" && approval.resolved_at && (
                <span className="text-xs text-text-muted shrink-0">
                  {formatDate(approval.resolved_at, "MMM d")}
                </span>
              )}
            </Card>
          ))}
        </div>
      )}
    </motion.div>
  );
}
